import React, { Component, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useReducedMotion } from '../../animations';
import { ArrowLeft, ArrowRight } from '../../design/icons';
import { useTheme } from '../../theme/ThemeContext';
import { ChoiceGroup } from '../../design/ui';
import BayerDither from '../../design/BayerDither';
import type { ForgeFocus, ForgeSceneProps } from './ForgeScene';
import WorkbenchFilm from '../Media/WorkbenchFilm';
import './forge-model.css';

function SceneLoadFailed({ onFailure }: ForgeSceneProps) {
    useEffect(onFailure, [onFailure]);
    return null;
}

const ForgeScene = lazy(() => import('./ForgeScene').catch(() => ({ default: SceneLoadFailed })));

/** A scene failure leaves the workbench poster and profile links in place. */
class SceneBoundary extends Component<{ children: React.ReactNode; onFailure: () => void }, { failed: boolean }> {
    state = { failed: false };
    static getDerivedStateFromError() { return { failed: true }; }
    componentDidCatch() { this.props.onFailure(); }
    render() { return this.state.failed ? null : this.props.children; }
}

export default function ForgeModel() {
    const { resolvedMode } = useTheme();
    const reducedMotion = useReducedMotion();
    const host = useRef<HTMLDivElement>(null);
    const [entered, setEntered] = useState(false);
    const [active, setActive] = useState(false);
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);
    const [focus, setFocus] = useState<ForgeFocus>('overview');
    const [yaw, setYaw] = useState(0);
    const [dragging, setDragging] = useState(false);
    const dragStart = useRef<{ x: number; yaw: number } | null>(null);
    const onReady = useCallback(() => setReady(true), []);
    const onFailure = useCallback(() => { setFailed(true); setReady(false); }, []);

    useEffect(() => {
        let inView = false;
        const sync = () => {
            const visible = inView && !document.hidden;
            setActive(visible);
            if (visible) setEntered(true);
        };
        const observer = new IntersectionObserver(entries => {
            inView = entries.some(entry => entry.isIntersecting);
            sync();
        }, { rootMargin: '80px' });
        if (host.current) observer.observe(host.current);
        document.addEventListener('visibilitychange', sync);
        return () => {
            observer.disconnect();
            document.removeEventListener('visibilitychange', sync);
        };
    }, []);

    const interactive = ready && !failed;
    const detail = WORK[focus];
    const angleDeg = ((Math.round((yaw * 180) / Math.PI) % 360) + 360) % 360;

    return (
        <figure className="forge-model" aria-labelledby="forge-model-title">
            <div className="forge-model__intro">
                <div>
                    <h2 id="forge-model-title">Abhijith&rsquo;s workbench</h2>
                    <p>Enterprise systems. AI agents. Down to the silicon.</p>
                </div>
                <div className="forge-model__telemetry" aria-hidden="true">
                    <span className="forge-model__telemetry-dot" />
                    <span>STAGE // {focus.toUpperCase()}</span>
                    <span>{angleDeg}&deg;</span>
                </div>
            </div>
            <div
                className={`forge-model__viewport${dragging ? ' is-dragging' : ''}`}
                ref={host}
                data-ready={interactive}
                onPointerDown={event => {
                    if (!interactive) return;
                    event.currentTarget.setPointerCapture?.(event.pointerId);
                    dragStart.current = { x: event.clientX, yaw };
                    setDragging(true);
                }}
                onPointerMove={event => {
                    if (!dragStart.current || !interactive) return;
                    const dx = event.clientX - dragStart.current.x;
                    setYaw(dragStart.current.yaw + dx * 0.0085);
                }}
                onPointerUp={() => { dragStart.current = null; setDragging(false); }}
                onPointerCancel={() => { dragStart.current = null; setDragging(false); }}
            >
                <span className="forge-model__corner forge-model__corner--tl" aria-hidden="true" />
                <span className="forge-model__corner forge-model__corner--tr" aria-hidden="true" />
                <span className="forge-model__corner forge-model__corner--bl" aria-hidden="true" />
                <span className="forge-model__corner forge-model__corner--br" aria-hidden="true" />
                <BayerDither />
                <img className="forge-model__poster"
                    src={`/sculptures/abhijith-workbench-${resolvedMode}.webp`} width="1100" height="850"
                    alt="Abhijith's workbench: enterprise platform cartridges, connected agent characters within guardrails, and a chip with circuit traces, joined by copper pathways."
                    decoding="async" />
                {entered && !failed && (
                    <div className="forge-model__canvas" aria-hidden="true">
                        <SceneBoundary onFailure={onFailure}>
                            <Suspense fallback={null}>
                                <ForgeScene focus={focus} yaw={yaw} reducedMotion={reducedMotion}
                                    active={active} dark={resolvedMode === 'dark'} onReady={onReady} onFailure={onFailure} />
                            </Suspense>
                        </SceneBoundary>
                    </div>
                )}
            </div>
            <ChoiceGroup<ForgeFocus> className="forge-model__focus" label="Explore Abhijith's workbench"
                value={focus} onChange={setFocus}
                options={[
                    { value: 'overview', label: 'All' },
                    { value: 'systems', label: 'Systems' },
                    { value: 'agents', label: 'Agents' },
                    { value: 'hardware', label: 'Hardware' },
                ]} />
            <div className="forge-model__controls" role="group" aria-label="Inspect the workbench model">
                <span>{interactive ? 'Drag or turn to inspect.' : failed ? 'Static view' : 'Opening the workbench…'}</span>
                <div className="forge-model__turn">
                    <button type="button" aria-label="Rotate workbench left" disabled={!interactive}
                        onClick={() => setYaw(value => value - Math.PI / 6)}><ArrowLeft size={16} /></button>
                    <button type="button" aria-label="Rotate workbench right" disabled={!interactive}
                        onClick={() => setYaw(value => value + Math.PI / 6)}><ArrowRight size={16} /></button>
                    <button type="button" onClick={() => { setYaw(0); setFocus('overview'); }}>Reset</button>
                </div>
            </div>
            <figcaption className="forge-model__caption">
                <div aria-live="polite" aria-atomic="true">
                    <div className="forge-model__caption-head">
                        <h3>{detail.title}</h3>
                        <span className="forge-model__caption-spec">{detail.spec}</span>
                    </div>
                    <p>{detail.description}</p>
                    <Link to={detail.to}>{detail.action}<ArrowRight size={15} /></Link>
                </div>
                <WorkbenchFilm />
            </figcaption>
        </figure>
    );
}

// These facets come from the profile, skills, and writing in this repository.
const WORK: Record<ForgeFocus, { title: string; spec: string; description: string; to: string; action: string }> = {
    overview: {
        title: 'The connections are the work.',
        spec: 'CRM · Agents · Silicon',
        description: 'I bridge enterprise platforms and AI, then explore the models and hardware underneath. Three parts of the same curiosity.',
        to: '/about', action: 'Meet the builder',
    },
    systems: {
        title: 'Platforms that work together.',
        spec: 'Salesforce · Event Mesh · Cloud',
        description: 'Salesforce, cloud, and event-driven integrations. My career started in enterprise CRM and grew into connecting whole systems.',
        to: '/about', action: 'Explore my experience',
    },
    agents: {
        title: 'Agents built for production.',
        spec: 'LangGraph · Google ADK · Guardrails',
        description: 'LangGraph, Google ADK, and retrieval pipelines—with validation, human review, and observability built around the work.',
        to: '/article/agentic-ai-salesforce', action: 'Read about agent systems',
    },
    hardware: {
        title: 'Learning below the abstraction.',
        spec: 'ESP32-S3 · iCE40 FPGA · RTL',
        description: 'ESP32-S3, iCE40 FPGA, and tiny-gpu RTL. Personal experiments that take me from model runtimes down to the silicon.',
        to: '/article/ml-formats-and-runtimes', action: 'Explore on-device ML',
    },
};
